import {
    ANDROID_OUTLOOK,
    IOS_OUTLOOK,
    TEAMS,
    TEAMS_HUB,
    ANDROID_TEAMS,
    IOS_TEAMS,
    OUTLOOK_EXTENSION,
} from 'owa-config';
import type { MiniHostApps } from 'mini-opx-config/lib/types/MiniHostApps';

export type MiniHostAppFlag = {
    default: boolean;
} & {
    [key in MiniHostApps]?: boolean;
};

export type MiniHostAppFeature =
    | 'default'
    | 'campaigns'
    | 'headerAuth'
    | 'nativeFeedback'
    | 'readOnlyReadingPane'
    | 'substrateSearch'
    | 'native'
    | 'ads'
    | 'adsHonorGlobalPrivacyControl'
    | 'loadCultureFromHost'
    | 'adsInFocussedInbox'
    | 'syncOwaUserConfigCache'
    | 'validateBootCache'
    | 'offlineSync'
    | 'multiAccount'
    | 'swipeRefresh'
    | 'zoom'
    | 'overrideShowAdPreferencesChecks'
    | 'subscribeForHostUpdates'
    | 'inboxShopping'
    | 'miniMeControl'
    | 'referFriendsAndFamily'
    | 'nativePushNotification'
    | 'allowTenantBlocking'
    | 'opxComponentLifecycle'
    | 'componentTokenProvider'
    | 'opxDeeplink'
    | 'multipleAds'
    | 'loadThemeFromHostApp'
    | 'authRedirectOnSessionTimeout'
    | 'nativeHost'
    | 'adPrivacyOptionLoadOverride'
    | 'sendInAppFeedbackSubmittedSignal'
    | 'nativeUserProvider'
    | 'nativeServiceWorker'
    | 'nativePerfLog'
    | 'hostCampaigns'
    | 'nativeDiagnostics'
    | 'vernacularCompose'
    | 'miniEventReminders'
    | 'nativeModuleSwitcher'
    | 'showUSQHeader'
    | 'shareViaOutlookLite'
    | 'showNPS'
    | 'adShimmer'
    | 'copilot'
    | 'fontScale'
    | 'pinchToZoom'
    | 'msalTokenFetch';

export const MINI_HOST_APP_FLAGS: Record<MiniHostAppFeature, MiniHostAppFlag> = {
    default: {
        default: true,
        native: true,
    },
    ads: {
        default: true,
        native: true,
    },
    adsHonorGlobalPrivacyControl: {
        default: true,
        native: false,
    },
    campaigns: {
        default: false,
        native: false,
        outlooklite: true, // Enable for this client by overriding default config of native
    },
    headerAuth: {
        default: false,
        native: true,
    },
    native: {
        default: false,
        native: true,
    },
    nativeFeedback: {
        default: false,
        native: true,
    },
    readOnlyReadingPane: {
        default: false,
        teams: true,
        androidteams: true,
        iosteams: true,
    },
    substrateSearch: {
        default: true,
        native: true,
    },
    loadCultureFromHost: {
        default: false,
        native: true,
    },
    adsInFocussedInbox: {
        default: true,
        native: false,
    },
    syncOwaUserConfigCache: {
        default: false,
        native: true,
    },
    validateBootCache: {
        default: false,
        outlooklite: true,
    },
    offlineSync: {
        default: false,
        outlooklite: true,
    },
    multiAccount: {
        default: false,
        outlooklite: true,
    },
    swipeRefresh: {
        default: false,
        outlooklite: true,
    },
    zoom: {
        default: false,
        outlooklite: true,
    },
    overrideShowAdPreferencesChecks: {
        default: false,
        androidoutlook: true,
        iosoutlook: true,
        outlooklite: false,
    },
    subscribeForHostUpdates: {
        default: false,
        outlooklite: true,
    },
    inboxShopping: {
        default: true,
        outlooklite: false,
    },
    allowTenantBlocking: {
        default: false,
        outlooklite: true,
    },
    miniMeControl: {
        default: true,
        outlooklite: false,
    },
    referFriendsAndFamily: {
        default: false,
        outlooklite: true,
    },
    nativePushNotification: {
        default: false,
        outlooklite: true,
    },
    opxComponentLifecycle: {
        default: false,
        opx: true,
    },
    componentTokenProvider: {
        default: false,
        opx: true,
        outlooklite: true,
    },
    opxDeeplink: {
        default: false,
        opx: true,
    },
    loadThemeFromHostApp: {
        default: false,
        opx: true,
        outlooklite: true,
    },
    authRedirectOnSessionTimeout: {
        default: true,
        outlooklite: false,
        opx: false,
    },
    nativeHost: {
        default: false,
        outlooklite: true,
        [ANDROID_OUTLOOK]: true,
        [IOS_OUTLOOK]: true,
        [TEAMS]: true,
        [TEAMS_HUB]: true,
        [ANDROID_TEAMS]: true,
        [IOS_TEAMS]: true,
        [OUTLOOK_EXTENSION]: true,
    },
    adPrivacyOptionLoadOverride: {
        default: false,
        opx: true,
        outlooklite: true,
    },
    sendInAppFeedbackSubmittedSignal: {
        default: false,
        outlooklite: true,
    },
    nativeUserProvider: {
        default: false,
        outlooklite: true,
    },
    nativeServiceWorker: {
        default: false,
        outlooklite: true,
    },
    nativePerfLog: {
        default: false,
        outlooklite: true,
    },
    hostCampaigns: {
        default: false,
        outlooklite: true,
    },
    multipleAds: {
        default: true,
        outlooklite: false,
    },
    nativeDiagnostics: {
        default: false,
        outlooklite: true,
    },
    vernacularCompose: {
        default: false,
        outlooklite: true,
    },
    miniEventReminders: {
        default: true,
        outlooklite: false,
    },
    nativeModuleSwitcher: {
        default: false,
        outlooklite: true,
    },
    showUSQHeader: {
        default: true,
        [ANDROID_OUTLOOK]: false,
        [IOS_OUTLOOK]: false,
    },
    shareViaOutlookLite: {
        default: false,
        outlooklite: true,
    },
    showNPS: {
        default: true,
        native: false,
        outlooklite: false,
        teams: false,
        androidteams: false,
        iosteams: false,
        opx: false,
    },
    adShimmer: {
        default: false,
        outlooklite: true,
    },
    copilot: {
        default: false,
        native: false,
        outlooklite: false,
        opx: false,
        outlookextension: false,
    },
    fontScale: {
        default: false,
        outlooklite: true,
    },
    pinchToZoom: {
        default: false,
        outlooklite: true,
    },
    msalTokenFetch: {
        default: true,
    },
};
